// ============================================
// Heat Haze Post-Process Effect
// ============================================
// Rising refraction shimmer driven by scrolling simplex noise —
// deserts, fires, jet exhaust, fever dreams.

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { HEAT_HAZE_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Heat Haze post-processing effect
 */
export class HeatHazeEffect extends PostProcessEffect {
  readonly type = 'heatHaze';
  readonly displayName = 'Heat Haze';

  // Parameters
  strength = 3.0;      // displacement in ‰ of the screen
  scale = 3.0;         // noise feature scale
  speed = 1.0;         // convection speed
  verticalFade = 0.0;  // -1 top-heavy … 0 uniform … 1 bottom-heavy

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = HEAT_HAZE_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'strength', type: 'float', min: 0, max: 10, default: 3.0, description: 'Displacement strength' },
      { name: 'scale', type: 'float', min: 0.5, max: 10, default: 3.0, description: 'Ripple scale' },
      { name: 'speed', type: 'float', min: 0, max: 5, default: 1.0, description: 'Convection speed' },
      { name: 'verticalFade', type: 'float', min: -1, max: 1, default: 0.0, description: 'Fade (-1=top, 0=uniform, 1=bottom)' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'strength': return this.strength;
      case 'scale': return this.scale;
      case 'speed': return this.speed;
      case 'verticalFade': return this.verticalFade;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'strength': this.strength = Math.max(0, Math.min(20, value)); break;
      case 'scale': this.scale = Math.max(0.1, Math.min(20, value)); break;
      case 'speed': this.speed = Math.max(0, Math.min(10, value)); break;
      case 'verticalFade': this.verticalFade = Math.max(-1, Math.min(1, value)); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const heatHazeWgsl = getCompiledShader('postprocess/heat-haze');
    this.shader = ctx.createShader(heatHazeWgsl, heatHazeWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'heat-haze-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'heat-haze-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, time, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      strength: this.strength * intensity,
      scale: this.scale,
      speed: this.speed,
      verticalFade: this.verticalFade,
      // Wrapped so the noise scroll keeps float precision on long sessions. The wrap period is
      // large enough (256s) that the visual jump at the seam is a sub-pixel pop.
      time: time % 256.0,
      aspect: width / Math.max(height, 1),
      _pad: [0, 0],
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
